import { useState } from "react";
import { dropshipProducts } from "@/mocks/dropship";
import MessageModal from "./MessageModal";

interface User { name: string; email: string; role: string; phone?: string; }
interface Props { user: User; onLogout: () => void; }

interface NewProduct {
  name: string;
  description: string;
  price: string;
  commission: string;
  category: string;
  stock: string;
  warehouseLocation: string;
  imageFile: string;
}

interface SellerOrder {
  id: string;
  product: string;
  productImage: string;
  buyer: string;
  buyerEmail: string;
  qty: number;
  total: number;
  shippingFee: number;
  status: "Pending" | "Processing" | "Shipped" | "Delivered";
  date: string;
  trackingNumber: string;
  deliveryAddress: string;
}

const emptyProduct: NewProduct = {
  name: "", description: "", price: "", commission: "15",
  category: "", stock: "", warehouseLocation: "", imageFile: "",
};

const initialOrders: SellerOrder[] = [
  {
    id: "#DS-8821", product: "Wireless Earbuds Pro X200",
    productImage: "https://public.readdy.ai/ai/img_res/974d7681f1bc9bd15842bdd98ca03739.jpg",
    buyer: "Sarah Johnson", buyerEmail: "sarah@email.com", qty: 2, total: 69.97, shippingFee: 9.99,
    status: "Delivered", date: "Apr 8, 2026", trackingNumber: "1ZXYZ4821A0012345",
    deliveryAddress: "456 Oak Ave, Los Angeles, CA 90001",
  },
  {
    id: "#DS-8820", product: "Vitamin C Serum 30ml",
    productImage: "https://readdy.ai/api/search-image?query=vitamin%20C%20brightening%20serum%20skincare%20bottle%2030ml%20on%20clean%20white%20background%20professional%20product%20photography%20beauty%20cosmetics%20wellness%20premium&width=400&height=300&seq=ds2&orientation=landscape",
    buyer: "Mike Torres", buyerEmail: "mike@email.com", qty: 3, total: 64.49, shippingFee: 8.99,
    status: "Shipped", date: "Apr 7, 2026", trackingNumber: "1ZABC9912B0067890",
    deliveryAddress: "789 Pine St, New York, NY 10001",
  },
  {
    id: "#DS-8819", product: "LED RGB Strip Light 10m",
    productImage: "https://readdy.ai/api/search-image?query=LED%20strip%20light%20RGB%20colorful%20glowing%20room%20decoration%20on%20clean%20dark%20background%20professional%20product%20photography%20lighting%20electronics%20decoration%20premium&width=400&height=300&seq=ds3&orientation=landscape",
    buyer: "David Kim", buyerEmail: "david@email.com", qty: 1, total: 23.98, shippingFee: 8.99,
    status: "Processing", date: "Apr 7, 2026", trackingNumber: "",
    deliveryAddress: "321 Elm St, Chicago, IL 60601",
  },
  {
    id: "#DS-8818", product: "Collagen Face Cream 50ml",
    productImage: "https://readdy.ai/api/search-image?query=collagen%20face%20cream%2050ml%20jar%20skincare%20anti%20aging%20on%20clean%20white%20background%20professional%20product%20photography%20beauty%20cosmetics%20premium&width=400&height=300&seq=ds5&orientation=landscape",
    buyer: "Priya Sharma", buyerEmail: "priya@email.com", qty: 2, total: 58.97, shippingFee: 8.99,
    status: "Pending", date: "Apr 6, 2026", trackingNumber: "",
    deliveryAddress: "555 Maple Dr, Houston, TX 77001",
  },
  {
    id: "#DS-8817", product: "Portable Mini Projector 1080P",
    productImage: "https://readdy.ai/api/search-image?query=portable%20mini%20projector%201080P%20home%20cinema%20on%20clean%20white%20background%20professional%20product%20photography%20electronics%20entertainment%20premium&width=400&height=300&seq=ds6&orientation=landscape",
    buyer: "Carlos Mendez", buyerEmail: "carlos@email.com", qty: 1, total: 98.98, shippingFee: 8.99,
    status: "Pending", date: "Apr 6, 2026", trackingNumber: "",
    deliveryAddress: "888 Beach Blvd, Miami, FL 33101",
  },
];

const statusColors: Record<string, string> = {
  Delivered: "bg-green-100 text-green-600",
  Shipped: "bg-sky-100 text-sky-600",
  Processing: "bg-amber-100 text-amber-600",
  Pending: "bg-gray-100 text-gray-500",
};

export default function SellerDashboard({ user, onLogout }: Props) {
  const [tab, setTab] = useState<"products" | "orders" | "upload" | "messages">("products");
  const [form, setForm] = useState<NewProduct>(emptyProduct);
  const [uploaded, setUploaded] = useState(false);
  const [previewImg, setPreviewImg] = useState<string>("");
  const [charCount, setCharCount] = useState(0);
  const [orders, setOrders] = useState<SellerOrder[]>(initialOrders);
  const [editingTracking, setEditingTracking] = useState<string | null>(null);
  const [trackingInput, setTrackingInput] = useState("");
  const [messagingBuyer, setMessagingBuyer] = useState<SellerOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("All");

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviewImg(ev.target?.result as string);
      reader.readAsDataURL(file);
      setForm(f => ({ ...f, imageFile: file.name }));
    }
  };

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.category) return;
    setUploaded(true);
    setTimeout(() => { setUploaded(false); setForm(emptyProduct); setPreviewImg(""); setCharCount(0); setTab("products"); }, 2500);
  };

  const handleSaveTracking = (orderId: string) => {
    if (!trackingInput.trim()) return;
    setOrders(prev => prev.map(o =>
      o.id === orderId ? { ...o, trackingNumber: trackingInput.trim(), status: "Shipped" } : o
    ));
    setEditingTracking(null);
    setTrackingInput("");
  };

  const handleStatusChange = (orderId: string, newStatus: SellerOrder["status"]) => {
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
  };

  const filteredOrders = statusFilter === "All" ? orders : orders.filter(o => o.status === statusFilter);

  const sellerStats = [
    { label: "Products Listed", value: dropshipProducts.length, icon: "ri-store-2-line", color: "bg-amber-50 text-amber-500" },
    { label: "Total Orders", value: orders.length, icon: "ri-shopping-bag-3-line", color: "bg-green-50 text-green-500" },
    { label: "Est. Revenue", value: "$" + orders.reduce((a, o) => a + o.total, 0).toFixed(0), icon: "ri-money-dollar-circle-line", color: "bg-sky-50 text-sky-500" },
    { label: "Pending Ship", value: orders.filter(o => o.status === "Pending" || o.status === "Processing").length, icon: "ri-truck-line", color: "bg-rose-50 text-rose-500" },
  ];

  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-store-2-line text-white text-xl"></i>
          </div>
          <div>
            <p className="text-white/80 text-xs">Factory / Seller Dashboard</p>
            <h3 className="text-white font-bold">{user.name}</h3>
            <p className="text-white/60 text-xs">{user.phone || user.email}</p>
          </div>
        </div>
        <button onClick={onLogout} className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-logout-box-line"></i> Sign Out
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b border-gray-100">
        {sellerStats.map(s => (
          <div key={s.label} className="text-center">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-2 ${s.color}`}>
              <i className={`${s.icon} text-lg`}></i>
            </div>
            <div className="text-xl font-extrabold text-gray-900">{s.value}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 px-5 overflow-x-auto">
        {([
          ["products", "ri-store-2-line", "My Products"],
          ["orders", "ri-shopping-bag-3-line", "Orders"],
          ["upload", "ri-upload-cloud-line", "Upload Product"],
          ["messages", "ri-message-3-line", "Messages"],
        ] as const).map(([t, icon, label]) => (
          <button key={t} onClick={() => setTab(t)} className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${tab === t ? "border-amber-400 text-amber-500" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
            <i className={icon}></i> {label}
          </button>
        ))}
      </div>

      <div className="p-5">
        {/* Products tab */}
        {tab === "products" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Your Dropship Products</h3>
              <button onClick={() => setTab("upload")} className="bg-amber-400 hover:bg-amber-500 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-add-line mr-1"></i> Add Product
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dropshipProducts.map(p => (
                <div key={p.id} className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="w-full h-36 overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-3">
                    <h4 className="font-semibold text-gray-800 text-sm line-clamp-1 mb-1">{p.name}</h4>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-amber-500 font-bold">${p.price}</span>
                      <span className="text-green-600 font-semibold">{p.commission}% commission</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span><i className="ri-stack-line mr-0.5"></i>{p.stock.toLocaleString()} in stock</span>
                      <span><i className="ri-shopping-bag-3-line mr-0.5"></i>{p.sold.toLocaleString()} sold</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders tab */}
        {tab === "orders" && (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-bold text-gray-800">Customer Orders</h3>
              <div className="flex gap-2 flex-wrap">
                {["All", "Pending", "Processing", "Shipped", "Delivered"].map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      statusFilter === s ? "bg-amber-400 text-white border-amber-400" : "bg-white text-gray-500 border-gray-200 hover:border-amber-300"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {filteredOrders.map(o => (
                <div key={o.id} className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-3 p-4 bg-gray-50">
                    <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                      <img src={o.productImage} alt={o.product} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-amber-500 font-bold text-xs">{o.id}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[o.status]}`}>{o.status}</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-800 truncate">{o.product}</p>
                      <p className="text-xs text-gray-400">{o.buyer} · Qty: {o.qty} · {o.date}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-gray-800">${o.total.toFixed(2)}</div>
                      <div className="text-xs text-gray-400">+${o.shippingFee} ship</div>
                    </div>
                  </div>

                  <div className="px-4 py-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-3">
                      <i className="ri-map-pin-line mr-1 text-amber-400"></i>{o.deliveryAddress}
                    </p>

                    {/* Tracking number */}
                    {o.trackingNumber ? (
                      <div className="bg-amber-50 rounded-lg px-3 py-2 mb-3 flex items-center justify-between">
                        <div>
                          <p className="text-xs text-amber-600 font-semibold">UPS Tracking</p>
                          <p className="text-xs font-mono font-bold text-gray-800">{o.trackingNumber}</p>
                        </div>
                        <button
                          onClick={() => { setEditingTracking(o.id); setTrackingInput(o.trackingNumber); }}
                          className="text-amber-500 hover:text-amber-600 cursor-pointer text-xs font-semibold"
                        >
                          Edit
                        </button>
                      </div>
                    ) : (
                      <div className="mb-3">
                        {editingTracking === o.id ? (
                          <div className="flex gap-2">
                            <input
                              value={trackingInput}
                              onChange={e => setTrackingInput(e.target.value)}
                              placeholder="Enter UPS tracking number..."
                              className="flex-1 border border-amber-300 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-400 font-mono"
                            />
                            <button
                              onClick={() => handleSaveTracking(o.id)}
                              className="bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingTracking(null)}
                              className="border border-gray-200 text-gray-500 text-xs font-bold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingTracking(o.id); setTrackingInput(""); }}
                            className="flex items-center gap-1.5 border border-dashed border-amber-300 text-amber-600 text-xs font-semibold px-3 py-2 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-add-line"></i> Add UPS Tracking Number
                          </button>
                        )}
                      </div>
                    )}

                    {/* Status update + message */}
                    <div className="flex gap-2 flex-wrap items-center">
                      <select
                        value={o.status}
                        onChange={e => handleStatusChange(o.id, e.target.value as SellerOrder["status"])}
                        className="border border-gray-200 rounded-full px-3 py-1.5 text-xs font-semibold outline-none focus:border-amber-400 cursor-pointer bg-white"
                      >
                        <option value="Pending">Pending</option>
                        <option value="Processing">Processing</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                      </select>
                      <button
                        onClick={() => setMessagingBuyer(o)}
                        className="flex items-center gap-1.5 border border-amber-300 text-amber-600 hover:bg-amber-50 text-xs font-bold px-4 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-message-3-line"></i> Message Buyer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload tab */}
        {tab === "upload" && (
          <div className="max-w-xl">
            <h3 className="font-bold text-gray-800 mb-4">Upload New Product</h3>
            {uploaded ? (
              <div className="flex flex-col items-center py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                  <i className="ri-checkbox-circle-fill text-green-500 text-3xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800 mb-1">Product Uploaded!</h4>
                <p className="text-gray-400 text-sm">Your product is now live for buyers worldwide.</p>
              </div>
            ) : (
              <form onSubmit={handleUpload} className="flex flex-col gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Photo *</label>
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-amber-400 transition-colors bg-gray-50 overflow-hidden">
                    {previewImg ? (
                      <img src={previewImg} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <i className="ri-image-add-line text-3xl"></i>
                        <span className="text-sm">Click to upload product photo</span>
                        <span className="text-xs">JPG, PNG up to 10MB</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                  </label>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Name *</label>
                  <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Wireless Earbuds Pro X200" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Description * <span className="text-gray-400 font-normal">({charCount}/500)</span></label>
                  <textarea
                    required
                    value={form.description}
                    onChange={e => { if (e.target.value.length <= 500) { setForm(f => ({ ...f, description: e.target.value })); setCharCount(e.target.value.length); }}}
                    placeholder="Describe your product features, specifications, and benefits..."
                    rows={4}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                  />
                  {charCount >= 500 && <p className="text-red-500 text-xs mt-1">Maximum 500 characters reached.</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Selling Price (USD) *</label>
                    <input required type="number" min="0.01" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="29.99" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Commission Rate (%) *</label>
                    <input required type="number" min="1" max="50" value={form.commission} onChange={e => setForm(f => ({ ...f, commission: e.target.value }))} placeholder="15" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Category *</label>
                    <select required value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white">
                      <option value="">Select category</option>
                      {["Electronics", "Apparel", "Home & Garden", "Health & Beauty", "Sports & Fitness", "Auto Parts", "Food & Beverage", "Machinery"].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Stock Quantity *</label>
                    <input required type="number" min="1" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} placeholder="1000" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Warehouse Location *</label>
                  <select required value={form.warehouseLocation} onChange={e => setForm(f => ({ ...f, warehouseLocation: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white">
                    <option value="">Select warehouse</option>
                    {["Los Angeles, CA", "New York, NY", "Miami, FL", "Toronto, ON", "Vancouver, BC", "London, UK", "Frankfurt, Germany", "Rotterdam, Netherlands", "Paris, France", "Dubai, UAE", "Sydney, Australia", "Singapore", "Tokyo, Japan", "São Paulo, Brazil"].map(w => (
                      <option key={w}>{w}</option>
                    ))}
                  </select>
                </div>

                <button type="submit" className="bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-upload-cloud-line mr-2"></i>Upload Product
                </button>
              </form>
            )}
          </div>
        )}

        {/* Messages tab */}
        {tab === "messages" && (
          <div>
            <h3 className="font-bold text-gray-800 mb-4">Buyer Messages</h3>
            <div className="flex flex-col gap-3">
              {orders.map(o => (
                <div
                  key={o.id}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-amber-50 transition-colors"
                  onClick={() => setMessagingBuyer(o)}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full shrink-0">
                    <span className="text-sm font-bold text-amber-600">{o.buyer.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-gray-800 text-sm">{o.buyer}</span>
                      <span className="text-xs text-gray-400">{o.id}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">{o.product}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[o.status]}`}>{o.status}</span>
                    <i className="ri-arrow-right-s-line text-gray-400"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Message modal */}
      {messagingBuyer && (
        <MessageModal
          productName={messagingBuyer.product}
          sellerName={messagingBuyer.buyer}
          sellerFlag="👤"
          orderId={messagingBuyer.id}
          buyerName={user.name}
          onClose={() => setMessagingBuyer(null)}
        />
      )}
    </div>
  );
}
